<script setup>

/**
 *  LIST SELECTOR
 * 
 *  Это универсальный компонент для отображения листа (списка) элементов
 *  с возможностью их добавления и удаления из массива данных 
 *  родительского компонента через события emit. 
 * 
 *  ВХОДЯЩИЕ ПАРАМЕТРЫ:
 * 
 *      sourceList  
 *          Указатель на экшен стора который нужно использовать для запроса листа
 * 
 *      sourceListNames 
 *          Массив с именами свойств элементов (полученных после вызова экшена стора) 
 *          для вывода в строках листа. Если свойства находятся в объекте не на первом уровне
 *          нужно рзделить имена свойства в пути точками
 *              ПРИМЕР ЗНАЧЕНИЙ: "uuid", "name", "data.imei"
 * 
 *      sourceListSelected
 *          Массив элементов, которые уже выбраны в родительском компоненте
 * 
 *  СОБЫТИЯ: 
 * 
 *      addItem
 *          Добавить элемент в массив данных родительского компонента 
 * 
 *      deleteItem
 *          Удалить элемент из массив данных родительского компонента 
 * 
 *  ПРИМЕР ВЫЗОВА:
 *  
 *      <UTAListSelector 
 *          :sourceList="carsStore.readCars"
 *          :sourceListNames="['name', 'data.vin', 'data.imei']"
 *          :sourceListSelected="selectedItems"
 *          @addItem="addDataItem"
 *          @deleteItem="deleteDataItem"
 *      />
 * 
 *  Этот компонент разрабатывался как супероружие, способное изменить ход разработки. 
 * 
 */

import { ref, computed, watch } from 'vue'
import { useTranslaterStore } from '@/stores/translater'

function checkSelected(uuid) {
    const isSelected = props.sourceListSelected.filter(sourceListItem => sourceListItem.uuid == uuid)
    return isSelected.length
}

async function getList(outerParams) {

    let newList

    let params = {
        'c.limit': 6
    }

    if(inputSearch.value.length > 2)
        params['c.q'] = inputSearch.value

    if(searchMode.value) 
        params['c.filterQ'] = searchMode.value

    if(outerParams) 
        params = { ...params, ...outerParams }

    await props.sourceList(params).then(response => {
        console.log('props.sourceList: ', response)
        if(response.array) {
            console.log('array: ', response.array)
            list.value = response
            newList = response
        }
    })

    return newList
}

function buildProp(obj, fieldName) {
    let value = obj
    fieldName
        .split('.')
        .map(name => value = value[name])
    return value
}

function changePage(cursor, dir) {
    const params = {
        'c.direction': dir, 
        'c.cursor': cursor
    }
    getList(params).then(items => {
        console.log(items)
        if(items.array) {
            list.value = items
            dir == 'after' ? 
                pageNumber.value++ : 
                pageNumber.value--
        }
    })
}

const props = defineProps({
    sourceList: {
        type: Function, 
        required: true
    },
    sourceListLabels: {
        type: Array
    }, 
    sourceListNames: {
        type: Array, 
        required: true
    }, 
    sourceListSelected: {
        type: Array
    }
})

const transitionStore = useTranslaterStore()
const getTranslate = computed(() => transitionStore.getTranslate)

const searchMode    = ref(null)
const inputSearch   = ref('')
const pageNumber    = ref(1)

const emits = defineEmits(['addItem', 'deleteItem'])

const list = ref([])

getList().then(result => {
    if(result.array) {
        list.value = result
    }
})


watch(inputSearch, () => {

    getList()
})

</script>

<template>
    <div class="uta-list-selector">
        <div class="uta-input__wrapper uta-search">
            <input 
                type="text" 
                class="form-control uta-input__wrapper-input uta-search__input uta-search__input--light"
                :placeholder="getTranslate.SEARCH + '...'" 
                v-model="inputSearch"
            >
            <template v-if="inputSearch.length > 2">
                <span class="material-icons uta-input__wrapper-icon" @click="inputSearch = '', fetchCars()">
                    cancel
                </span>
            </template>
            <template v-else>
                <span class="material-icons uta-input__wrapper-icon">
                    search
                </span>
            </template>
        </div>
        <div class="uta-list uta-list-selector__list mx-3" v-if="list.array">
            <div class="uta-list__title d-none d-lg-block">
                <div class="row">
                    <div class="col-10">
                        <div class="row">
                            <div 
                                v-for="(label, index) in sourceListLabels" 
                                :key="index"
                                :class="'col-' + 12 / sourceListLabels.length"
                            >
                                {{ getTranslate[label] }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="uta-list__content">
                <div 
                    v-for="(item, index) in list.array"
                    :key="index"
                    class="row uta-list__item uta-list-selector__list-item" 
                    :class="{ 'uta-list-selector__list-item--selected' : checkSelected(item.uuid) }"
                >
                    <div class="col-10">
                        
                        <div class="row">
                            <div 
                                v-for="(objName, index) in sourceListNames" 
                                :key="index"
                                :class="'col-' + 12 / sourceListNames.length"
                            >
                                <span class="uta-list-selector__list-item-prop">{{ buildProp(item, objName) || '-' }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="col-2 text-end">
                        <template v-if="checkSelected(item.uuid)">
                            <a href="#" @click.prevent="emits('deleteItem', item)">
                                <span class="material-symbols-outlined">
                                    check_box
                                </span>
                            </a>
                        </template>
                        <template v-else>
                            <a href="#" @click.prevent="emits('addItem', item)">
                                <span class="material-symbols-outlined">
                                    check_box_outline_blank
                                </span>
                            </a>
                        </template>
                    </div>
                </div>
            </div>
        </div>

        <div class="mt-4 uta-cars__pagination" v-if="list.c && (list.c.isBefore || list.c.isAfter)">
            <div class="row">
                <div class="col-12 col-xs-6 col-sm-6 col-md-4">
                    <div class="uta-pagination">
                        <button 
                            :disabled="!list.c.isBefore"
                            class="uta-btn uta-btn--sm uta-pagination__prev" 
                            @click.prevent="changePage(list.c.start, 'before')"
                        >
                            <span class="material-icons">
                                navigate_before
                            </span>
                        </button>
                        <div class="uta-pagination__current">
                            {{ pageNumber }}
                        </div>
                        <button 
                            :disabled="!list.c.isAfter"
                            class="uta-btn uta-btn--sm uta-pagination__next" 
                            @click.prevent="changePage(list.c.stop, 'after')"
                        >
                            <span class="material-icons">
                                navigate_next
                            </span>
                        </button>
                    </div>
                </div>
            </div>
        </div>

    </div>
</template>

<style lang="scss">
@import '@/assets/styles/scss/components/list-selector.scss';
</style>